import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../store/store";
import {
  fetchAllDrones,
  selectAllDrones,
  selectDronesLoading,
  selectDronesError,
  DroneDTO
} from "../../../../store/drone_slice";
import FilterComponent from "./components/filter/filter";
import DroneCard from "./components/drones/drones";
import Button from "../../../../common/button/button";
import Loading from "../../../../common/loading/loading";

interface DroneCatalogPageProps {
  searchText: string;
  selectedCity: string;
}

const DroneCatalogPage: React.FC<DroneCatalogPageProps> = ({
  searchText,
  selectedCity,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const drones: DroneDTO[] = useSelector(selectAllDrones) || [];
  const loading = useSelector(selectDronesLoading);
  const error = useSelector(selectDronesError);

  // Called when filter changes in FilterComponent
  const handleFilterChange = (selectedFilters: string[]) => {
    setActiveFilters(selectedFilters);
    setLastPageReached(false);
  };

  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const dronesPerRow = 6; // Cards per row in grid
  const pageSize = dronesPerRow * 2; // 2 rows per page
  const [page, setPage] = useState(0);
  const [lastPageReached, setLastPageReached] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Fetch drones when search parameters change
  useEffect(() => {
    dispatch(fetchAllDrones(0, pageSize, false, searchText, selectedCity));
    setLastPageReached(false);
    setPage(0);
    setInitialLoadComplete(false);
  }, [dispatch, searchText, selectedCity, pageSize]);

  // Set initial load complete when loading finishes
  useEffect(() => {
    if (!loading && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [loading, initialLoadComplete]);

  const dronesArray = Array.isArray(drones) ? drones : [];

  // Client-side filter by category only (server handles name/city search)
  let filteredDrones: DroneDTO[] = dronesArray;

  if (activeFilters.length > 0) {
    filteredDrones = filteredDrones.filter((drone: DroneDTO) =>
      Array.isArray(drone.categories) &&
      drone.categories.some((cat: string) => activeFilters.includes(cat))
    );
  }

  const dronesToDisplay = filteredDrones;



  const loadMoreDrones = async () => {
    if (loadingMore) return;
    
    setLoadingMore(true);
    const nextPage = page + 1;
    
    try {
      const result = await dispatch(fetchAllDrones(nextPage, pageSize, true, searchText, selectedCity));
      setPage(nextPage);
      
      if (Array.isArray(result) && result.length < pageSize) {
        setLastPageReached(true);
      }
      
    } finally {
      setLoadingMore(false);
    }
  };

  const handleShowMoreClick = () => {
    loadMoreDrones();
  };

  if (loading && !initialLoadComplete) {
    return <div className="text-center p-10">Loading drones...</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-500">Error loading drones: {error}</div>;
  }

  return (
    <div className="relative -mt-14 sm:-mt-12 lg:-mt-24 z-20">
      <div className="w-full">
        <FilterComponent onFilterChange={handleFilterChange} />
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6 px-6 md:px-10 lg:px-14 justify-items-center mt-4 sm:-mt-12 lg:-mt-24"
          style={{ willChange: 'scroll-position' }}
        >
          {dronesToDisplay.map((drone: DroneDTO) => {
            try {
              // Get image source - prefer imageKeys over imageUrls if available
              const imageSource = drone.imageKeys?.[0] || drone.imageUrls?.[0] || '';
              
              return (
                <DroneCard
                  key={drone.id}
                  id={drone.id.toString()}
                  imageUrl={imageSource}
                  droneName={drone.name}
                  location={drone.location}
                  timestamp={drone.dateTime}
                  userName={`${drone.postedBy?.firstName || ''} ${drone.postedBy?.lastName || ''}`.trim()}
                  pricePerDay={drone.price}
                  currency={drone.currency}
                />
              );
            } catch (error) {
              console.error('Error rendering drone card:', error);
              return null; // Skip rendering this card if there's an error
            }
          })}
        </div>
        {/* Show if not last page */}
        {!lastPageReached && (
          <div className="flex justify-center mt-4 pb-4">
            <Button
              onClick={handleShowMoreClick}
              buttonType="filter"
              className="bg-grayGlobal text-primary font-ubuntu font-bold px-4 py-2 rounded-lg"
              disabled={loadingMore}
            >
              {loadingMore ? (
                <div className="flex items-center">
                  <Loading size="small" text="" className="mr-2" />
                  <span>Loading...</span>
                </div>
              ) : (
                "Show More Listings"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DroneCatalogPage;
